import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Offcanvas, Form, Button, InputGroup } from "react-bootstrap";
import { X, Search, Check } from "lucide-react";
import { UnifiedDateTimePicker } from "@/components/DateInput/UnifiedDateTimePicker";
import { sheetStyles as sheetStylesFromFile } from "./FilterSheet.styles";
import { CustomSelect } from "@/components";
import type {
  Filter,
  FilterOption,
  FilterValue,
} from "@/components/Table/Filters";
import { Display_Value_Key as dvk } from "@/components/Create/utils";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import { useDebounce } from "@/utils";
import type { UserModel } from "@/database";
import {
  getUploadedByValue,
  type UploadedByValue,
} from "@/components/MediaList/uploadedBy";
import youtubeLogo from "@/assets/svg/yt-small.svg.png";
import facebookLogo from "@/assets/svg/fb-small.svg";
import instagramLogo from "@/assets/svg/insta-small.svg";
import twitterLogo from "@/assets/icons/x.png";
import linkedinLogo from "@/assets/svg/in-small.svg";
import denmarkLogo from "@/assets/icons/svg/denmark_fav.svg";

// Platform logos for the publishing_destinations picker.
// Keys match the filter option `value`s defined in FetchFilters.
const PUBLISHING_LOGOS: Record<string, string> = {
  denmark: denmarkLogo,
  youtube: youtubeLogo,
  facebook: facebookLogo,
  instagram: instagramLogo,
  twitter: twitterLogo,
  linkedin: linkedinLogo,
};

// Remaining fields rendered flat (no section headings) after the bespoke
// fields above. Order matters — more-used facets come first. Agency, Uploader
// (ctby) and Modifier (mtbyuser) are intentionally absent — Agency and Uploader
// are reachable from the smart-search scope menu; Modifier is retired.
const SECONDARY_ORDER = ["lang", "rangeMD"];

// Filter keys that render as tile grids (same visual language as Published to).
// We handle single + multi modes; backing filter type decides which.
const TILE_GRID_KEYS = new Set(["media_status", "source", "team", "media_ref"]);

// Display-name overrides applied at render time. Keep backend filter keys
// intact so the engine/URL contract doesn't shift.
const DISPLAY_LABEL_OVERRIDES: Record<string, string> = {
  media_ref: "Content Type",
};

// Per-filter option blocklists — hide values at render time without touching
// the shared filter config (other surfaces may still need the full list).
const OPTION_BLOCKLIST: Record<string, Set<string>> = {
  media_ref: new Set(["trimmed"]),
  media_status: new Set(["-2", "-3"]), // Deleted, Error
};

// Team brand allowlist. Matches each team option's label against these
// keywords; teams outside the set are hidden. Keeps the grid to a scan-friendly
// 4 tiles and swaps in short brand codes + distinctive badge colors.
interface TeamBrand {
  code: string;
  color: string;
  matches: RegExp;
}
const TEAM_BRANDS: TeamBrand[] = [
  { code: "TOI", color: "#D72222", matches: /\b(toi|times of india)\b/i },
  { code: "NBT", color: "#E7322D", matches: /\b(nbt|navbharat times)\b/i },
  { code: "ET", color: "#EA2E91", matches: /\b(et|economic times)\b/i },
  { code: "MT", color: "#CE5A14", matches: /\b(mt|maharashtra times)\b/i },
];

export interface FilterSheetProps {
  show: boolean;
  onHide: () => void;
  availableFilters: Filter[];
  appliedFilters: Record<string, FilterValue>;
  onApply: (next: Record<string, FilterValue>) => void;
  onClear: () => void;
  // If provided, limits the Team tile grid to these product/team ids. Derived
  // from the user's CMS permissions so editors only see teams they can work in.
  allowedTeamIds?: string[];
  onAutoSelectInputChange?: (params: { name: string; keyword: string }) => void;
  autoSelectOptionsMap?: Record<string, FilterOption[]>;
  autoSelectLoader?: boolean;
  // Used by the Me / My team quick presets in the Uploader section.
  currentUser?: UserModel | null;
}

const FilterSheet: React.FC<FilterSheetProps> = ({
  show,
  onHide,
  availableFilters,
  appliedFilters,
  onApply,
  onClear,
  allowedTeamIds,
  onAutoSelectInputChange,
  autoSelectOptionsMap,
  currentUser,
}) => {
  const location = useLocation();
  const isDiscoverRoute = location.pathname.startsWith("/discover");
  const activeTab = useMemo(() => {
    const tabParam = new URLSearchParams(location.search).get("tab");
    if (tabParam) return tabParam;
    return location.pathname.split("/").pop() || "";
  }, [location.pathname, location.search]);
  const isDiscoverContext = isDiscoverRoute || activeTab === "discover";
  const isLibraryTab = activeTab === "library";
  const isDriveTab = location.pathname.startsWith("/drive") || activeTab === "drive";

  const discoverVisibleFields = useMemo(() => {
    const names = availableFilters.map((f) => f.name);
    const firstSix = names.filter((n) => n !== "media_duration").slice(0, 6);
    return new Set([...firstSix, "media_duration", "team", "ctby"]);
  }, [availableFilters]);
  const libraryVisibleFields = useMemo(
    // Library tab shows: Media ID, Keyword, Uploader, Upload Date.
    // "Published Destinations" is intentionally excluded — it belongs to
    // the publish workflow, not the library browse experience.
    () => new Set(["id", "title", "ctby", "range"]),
    []
  );
  const driveVisibleFields = useMemo(
    () => new Set(["id", "title", "ctby", "range", "source"]),
    []
  );
  const shouldShowField = useCallback(
    (name: string) => {
      if (isDiscoverContext) return discoverVisibleFields.has(name);
      if (isDriveTab) return driveVisibleFields.has(name);
      if (isLibraryTab) return libraryVisibleFields.has(name);
      return true;
    },
    [
      isDiscoverContext,
      isLibraryTab,
      isDriveTab,
      discoverVisibleFields,
      libraryVisibleFields,
      driveVisibleFields,
    ]
  );
  const normalizedAllowedTeamIds = useMemo(
    () => (allowedTeamIds || []).map((id) => String(id)),
    [allowedTeamIds]
  );

  // Deferred-apply pattern: every selection inside the sheet stages locally;
  // the parent's `onApply` only fires on close, on Enter in a search field,
  // or on Clear all. Staging keeps the user's choices coherent and avoids
  // partial filter states leaking out before the user is done.
  //
  // `staged` drives the sheet's UI; `stagedRef` is its synchronous mirror
  // so commit handlers can both read-then-update without state batching
  // ordering bugs, and so the close-handler can read the very latest value
  // immediately after any in-flight commits.
  const [staged, setStaged] =
    useState<Record<string, FilterValue>>(appliedFilters);
  const stagedRef = useRef(staged);

  // Seed staged from applied each time the sheet opens. Closed → open is
  // the right boundary; we don't reset on every applied-filter change so
  // mid-open external mutations don't wipe the user's pending picks.
  useEffect(() => {
    if (show) {
      stagedRef.current = appliedFilters;
      setStaged(appliedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Refs into the deferred-commit text fields. The sheet flushes them on
  // close so any unfinished typing turns into a staged filter — users never
  // lose work by closing without pressing Enter.
  const idFieldRef = useRef<TextSearchFieldHandle>(null);
  const titleFieldRef = useRef<TextSearchFieldHandle>(null);

  const handleHide = useCallback(() => {
    idFieldRef.current?.flush();
    titleFieldRef.current?.flush();
    // Single commit point. After the flushes, stagedRef holds the latest.
    onApply(stagedRef.current);
    onHide();
  }, [onApply, onHide]);

  // Apply the current staged bag without closing — used when a text search
  // field is submitted via Enter. Reads stagedRef to pick up writes made
  // earlier in the same tick (the field's onCommit is synchronous on the ref).
  const applyNow = useCallback(() => {
    onApply(stagedRef.current);
  }, [onApply]);

  const filterByName = useMemo(() => {
    const map: Record<string, Filter> = {};
    availableFilters.forEach((f) => {
      map[f.name] = f;
    });
    return map;
  }, [availableFilters]);

  // --- Derived reads (source of truth while sheet is open = staged) ---

  const getMultiValues = (name: string): string[] => {
    const v = staged[name];
    if (Array.isArray(v)) return v as string[];
    if (v != null) return [String(v)];
    return [];
  };
  const getMultiLabels = (name: string): string[] => {
    const d = staged[name + dvk];
    if (Array.isArray(d)) return d as string[];
    if (d != null) return [String(d)];
    return getMultiValues(name);
  };
  const getSingleValue = (name: string): string => {
    const v = staged[name];
    return v == null ? "" : String(v);
  };
  const getDateValue = (name: string): any => staged[name];

  // --- Staged-write handlers (no parent callback, no apply) ---
  // Each handler updates stagedRef synchronously before triggering the
  // setState. That way, an immediate read of stagedRef from a follow-up
  // call (e.g. text-field flush followed by close-apply) sees the latest.

  const writeStaged = useCallback(
    (mutator: (next: Record<string, FilterValue>) => void) => {
      const next: Record<string, FilterValue> = { ...stagedRef.current };
      mutator(next);
      delete (next as any).page;
      stagedRef.current = next;
      setStaged(next);
    },
    []
  );

  const commitMulti = useCallback(
    (name: string, values: string[], labels: string[]) => {
      writeStaged((next) => {
        if (values.length === 0) {
          delete next[name];
          delete (next as any)[`${name}${dvk}`];
        } else {
          next[name] = values;
          (next as any)[`${name}${dvk}`] = labels;
        }
      });
    },
    [writeStaged]
  );

  const commitSingle = useCallback(
    (name: string, value: string, label: string) => {
      writeStaged((next) => {
        if (!value) {
          delete next[name];
          delete (next as any)[`${name}${dvk}`];
        } else {
          next[name] = value;
          (next as any)[`${name}${dvk}`] = label;
        }
      });
    },
    [writeStaged]
  );

  const commitDate = useCallback(
    (name: string, range: any, display: string) => {
      writeStaged((next) => {
        if (!range) {
          delete next[name];
          delete (next as any)[`${name}${dvk}`];
        } else {
          next[name] = range;
          (next as any)[`${name}${dvk}`] = display;
        }
      });
    },
    [writeStaged]
  );

  // Stage a free-text search against a single filter key. Used by the ID
  // and Title inputs (string-shape). Empty string clears the filter.
  // For the "id" key, comma-separated input is split into an array so the
  // backend receives multiple IDs as a list rather than a single string.
  const commitTextFilter = useCallback(
    (key: string, raw: string) => {
      writeStaged((next) => {
        const q = raw.trim();
        if (!q) {
          delete next[key];
          delete (next as any)[`${key}${dvk}`];
        } else if (key === "id") {
          const ids = q.split(",").map((s) => s.trim()).filter(Boolean);
          next[key] = ids.length === 1 ? ids[0] : ids;
          (next as any)[`${key}${dvk}`] = q;
        } else {
          next[key] = q;
          (next as any)[`${key}${dvk}`] = q;
        }
      });
    },
    [writeStaged]
  );

  const stagedUploadedBy: UploadedByValue = currentUser
    ? getUploadedByValue(staged, currentUser)
    : "all";

  const setAgencyPreset = (agencyId: string, agencyLabel: string) => {
    const activeIds = getMultiValues("agency").map(String);
    const activeLabels = getMultiLabels("agency");
    const id = String(agencyId);
    if (activeIds.includes(id)) {
      const idx = activeIds.indexOf(id);
      commitMulti(
        "agency",
        activeIds.filter((v) => v !== id),
        activeLabels.filter((_, i) => i !== idx)
      );
    } else {
      commitMulti("agency", [...activeIds, id], [...activeLabels, agencyLabel]);
    }
  };

  // Debounce the parent's user-search callback so rapid keystrokes in the
  // Uploader / Team / Agency dropdowns don't fire a user.list API call on
  // every character. CustomSelect already debounces at 200 ms; this adds a
  // further 400 ms gate at the FilterSheet level.
  const autoSelectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedAutoSelectInputChange = useCallback(
    (params: { name: string; keyword: string }) => {
      clearTimeout(autoSelectTimerRef.current);
      autoSelectTimerRef.current = setTimeout(() => {
        onAutoSelectInputChange?.(params);
      }, 400);
    },
    [onAutoSelectInputChange]
  );

  const appliedCount = useMemo(() => {
    const ignore = new Set(["page", "rows", "sort", "sortBy", "must_not"]);
    let n = 0;
    Object.keys(staged).forEach((k) => {
      if (k.endsWith(dvk)) return;
      if (ignore.has(k)) return;
      if (staged[k] == null) return;
      n += 1;
    });
    return n;
  }, [staged]);

  const handleClearAll = () => {
    trackEvent("browse", {
      category: "filters",
      entity: "FilterSheet",
      action: "clear-all",
      location: "content",
      ...getTrackUserContext(),
    });
    // Clear all is an explicit destructive gesture — apply immediately so
    // the chip strip and table clear at the same moment the sheet UI does.
    stagedRef.current = {};
    setStaged({});
    onClear();
    onHide();
  };

  const renderField = (name: string) => {
    const f = filterByName[name];
    if (!f) return null;

    const displayLabel =
      isDiscoverContext && f.name === "team"
        ? "Content used"
        : (DISPLAY_LABEL_OVERRIDES[f.name] ?? f.displayName);

    if (f.name === "team") {
      let options = f.options ?? [];
      const hasWildcardTeamAccess = normalizedAllowedTeamIds.includes("*");
      if (normalizedAllowedTeamIds.length > 0 && !hasWildcardTeamAccess) {
        const allowed = new Set(normalizedAllowedTeamIds);
        options = options.filter((o) => allowed.has(o.value));
      }
      const seen = new Set<string>();
      options = options.filter((o) => {
        if (seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      });
      if (options.length === 0) return null;

      // In discover context the "Content used" filter must match against
      // child_media.team — the API maps `content_used` to that nested field.
      const teamFilterKey = isDiscoverContext ? "content_used" : "team";

      const selectedValues = getMultiValues(teamFilterKey);
      const selectedLabels = getMultiLabels(teamFilterKey);
      const teamPresetOptions = options.slice(0, 3);

      return (
        <div key={name} className="fs-field">
          <label className="fs-field-label">{displayLabel}</label>
          {teamPresetOptions.length > 0 && (
            <div className="fs-uploader-presets">
              {teamPresetOptions.map((opt) => {
                const id = String(opt.value);
                const isActive = selectedValues.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`text-truncate fs-uploader-chip${isActive ? " is-active" : ""}`}
                    onClick={() => {
                      const activeIds = getMultiValues(teamFilterKey).map(String);
                      const activeLabels = getMultiLabels(teamFilterKey);
                      if (activeIds.includes(id)) {
                        const idx = activeIds.indexOf(id);
                        commitMulti(
                          teamFilterKey,
                          activeIds.filter((v) => v !== id),
                          activeLabels.filter((_, i) => i !== idx)
                        );
                      } else {
                        commitMulti(
                          teamFilterKey,
                          [...activeIds, id],
                          [...activeLabels, opt.label]
                        );
                      }
                    }}
                    aria-pressed={isActive}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          <CustomSelect
            showHeader={false}
            value={selectedValues}
            multiSelect={true}
            placeholder="Search teams"
            options={options}
            updatedOptions={options}
            creditData={selectedValues.map((v, i) => ({
              value: v,
              label: selectedLabels[i] ?? v,
            }))}
            handleChange={(newValue) => {
              const picked =
                (newValue as { value: string; label: string }[]) ?? [];
              commitMulti(
                teamFilterKey,
                picked.map((o) => String(o.value)),
                picked.map((o) => String(o.label))
              );
            }}
            hideArrow={true}
            loader={false}
          />
        </div>
      );
    }

    // Tile-grid path — uniform visual for status/source/team/content-type.
    if (TILE_GRID_KEYS.has(f.name)) {
      const isMulti =
        f.name !== "media_status" &&
        f.name !== "media_ref" &&
        (f.type === "multiselect" || f.type === "autoselect");
      let options = f.options ?? [];

      // Drop any per-filter blocked option values.
      const blocklist = OPTION_BLOCKLIST[f.name];
      if (blocklist) {
        options = options.filter((o) => !blocklist.has(o.value));
      }

      // Team-specific: scope to the user's CMS products. Branded teams get a
      // recognisable colored badge; everything else gets a neutral initial
      // chip so the visual language stays consistent across the grid.
      let teamBrandByValue: Record<string, TeamBrand> | null = null;
      if (f.name === "team") {
        // Permission payload can contain wildcard "*" or numeric IDs.
        // Only hard-filter team options when we have explicit IDs.
        const hasWildcardTeamAccess = normalizedAllowedTeamIds.includes("*");
        if (normalizedAllowedTeamIds.length > 0 && !hasWildcardTeamAccess) {
          const allowed = new Set(normalizedAllowedTeamIds);
          options = options.filter((o) => allowed.has(o.value));
        }
        // De-duplicate by team value just in case the source has repeats.
        const seen = new Set<string>();
        options = options.filter((o) => {
          if (seen.has(o.value)) return false;
          seen.add(o.value);
          return true;
        });
        // Sort: branded teams (TOI/NBT/ET/MT) first, in brand order; the rest
        // alphabetical so the long tail is predictable.
        const brandIndex = (label: string) => {
          const i = TEAM_BRANDS.findIndex((b) => b.matches.test(label));
          return i < 0 ? Number.MAX_SAFE_INTEGER : i;
        };
        options = [...options].sort((a, b) => {
          const ai = brandIndex(a.label);
          const bi = brandIndex(b.label);
          if (ai !== bi) return ai - bi;
          return a.label.localeCompare(b.label);
        });

        // Build the brand-by-value map for the leading badge renderer.
        teamBrandByValue = {};
        options.forEach((opt) => {
          const brand = TEAM_BRANDS.find((b) => b.matches.test(opt.label));
          if (brand) teamBrandByValue![opt.value] = brand;
        });
      }

      if (options.length === 0) return null;

      const selectedValues = isMulti
        ? getMultiValues(f.name)
        : (() => {
            const v = getSingleValue(f.name);
            return v ? [v] : [];
          })();
      const selectedLabels = isMulti ? getMultiLabels(f.name) : [];

      const handleChange = (values: string[], labels: string[]) => {
        if (isMulti) {
          commitMulti(f.name, values, labels);
        } else {
          commitSingle(f.name, values[0] ?? "", labels[0] ?? "");
        }
      };

      const handleClear = () => handleChange([], []);

      return (
        <div key={name} className="fs-field">
          <div className="fs-field-row">
            <label className="fs-field-label">{displayLabel}</label>
            {selectedValues.length > 0 && (
              <button
                type="button"
                className="fs-field-reset"
                onClick={handleClear}
              >
                Clear
              </button>
            )}
          </div>
          <TileGrid
            options={options}
            mode={isMulti ? "multi" : "single"}
            values={selectedValues}
            labels={selectedLabels}
            onChange={handleChange}
            searchPlaceholder={f.name === "team" ? "Search teams" : undefined}
            alwaysShowSearch={f.name === "team"}
            renderLeading={
              teamBrandByValue
                ? (opt) => {
                    const brand = teamBrandByValue![opt.value];
                    if (brand) {
                      return (
                        <span
                          className="fs-tile-badge"
                          style={{ background: brand.color }}
                          aria-hidden="true"
                        >
                          {brand.code}
                        </span>
                      );
                    }
                    // Neutral letter avatar for non-brand teams. Keeps
                    // the visual rhythm consistent across the grid.
                    const initial = (opt.label?.[0] ?? "·").toUpperCase();
                    return (
                      <span
                        className="fs-tile-badge fs-tile-badge-neutral"
                        aria-hidden="true"
                      >
                        {initial}
                      </span>
                    );
                  }
                : undefined
            }
          />
        </div>
      );
    }

    if (f.type === "multiselect" || f.type === "autoselect") {
      const values = getMultiValues(f.name);
      const labels = getMultiLabels(f.name);
      return (
        <div key={name} className="fs-field">
          <div className="fs-field-row">
            <label className="fs-field-label">{displayLabel}</label>
            {values.length > 0 && (
              <span className="fs-field-summary">{values.length} selected</span>
            )}
          </div>
          <MultiPicker
            filter={f}
            values={values}
            labels={labels}
            onChange={(v, l) => commitMulti(f.name, v, l)}
            supportsServerSearch={f.type === "autoselect"}
            onServerSearch={debouncedAutoSelectInputChange}
            dynamicOptions={autoSelectOptionsMap?.[f.name]}
          />
        </div>
      );
    }

    if (f.type === "select" || f.type === "radio") {
      return (
        <div key={name} className="fs-field">
          <label className="fs-field-label">{displayLabel}</label>
          <Form.Select
            size="sm"
            value={getSingleValue(f.name)}
            onChange={(e) => {
              const value = e.target.value;
              const opt = f.options?.find((o) => o.value === value);
              commitSingle(f.name, value, opt?.label ?? value);
            }}
          >
            <option value="">Any</option>
            {f.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </div>
      );
    }

    return null;
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleHide}
      placement="end"
      backdrop
      scroll={true}
      backdropClassName="filter-sheet-backdrop"
      className="filter-sheet"
      style={{ width: 348 }}
    >
      <style>{sheetStylesFromFile}</style>
      <Offcanvas.Header className="fs-header">
        <Offcanvas.Title className="fs-title">
          Filters
          {appliedCount > 0 && (
            <span className="fs-header-count">{appliedCount}</span>
          )}
        </Offcanvas.Title>
        <div className="fs-header-actions">
          {appliedCount > 0 && (
            <button
              type="button"
              className="fs-clear-link"
              onClick={handleClearAll}
            >
              Clear all
            </button>
          )}
          {appliedCount > 0 ? (
            <button
              type="button"
              className="fs-apply-btn"
              onClick={handleHide}
            >
              Apply
            </button>
          ) : (
            <button
              type="button"
              className="fs-close"
              onClick={handleHide}
              aria-label="Close filters"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0">
        <div className="fs-scroll">
          {/* Four discrete search fields — each owns its own filter key so
              the user knows exactly what they're searching. Text fields
              defer commit to Enter or sheet close (handled via flush refs). */}
          <div className="fs-primary">
            {shouldShowField("id") && (
              <TextSearchField
                ref={idFieldRef}
                label="Slike ID"
                placeholder="e.g. a1b2c3d4 — press Enter to apply"
                initialValue={getSingleValue("id")}
                onCommit={(v) => commitTextFilter("id", v)}
                onSubmit={applyNow}
              />
            )}
            {shouldShowField("title") && (
              <TextSearchField
                ref={titleFieldRef}
                label="Title & description"
                placeholder="Type and press Enter"
                initialValue={getSingleValue("title")}
                onCommit={(v) => commitTextFilter("title", v)}
                onSubmit={applyNow}
              />
            )}
            {shouldShowField("ctby") && filterByName["ctby"] && (
              <div className="fs-field">
                <label className="fs-field-label">Uploader</label>
                {currentUser && (
                  <div className="fs-uploader-presets">
                    <button
                      type="button"
                      className={`text-truncate fs-uploader-chip${
                        stagedUploadedBy === "myuploads" ? " is-active" : ""
                      }`}
                      onClick={() => {
                        const currentIds = getMultiValues("ctby");
                        const currentLabels = getMultiLabels("ctby");
                        const userId = String(currentUser.id);
                        if (currentIds.includes(userId)) {
                          const idx = currentIds.indexOf(userId);
                          commitMulti(
                            "ctby",
                            currentIds.filter((v) => v !== userId),
                            currentLabels.filter((_, i) => i !== idx)
                          );
                        } else {
                          commitMulti(
                            "ctby",
                            [...currentIds, userId],
                            [...currentLabels, currentUser.name ?? userId]
                          );
                        }
                      }}
                      aria-pressed={stagedUploadedBy === "myuploads"}
                    >
                      Me
                    </button>
                  </div>
                )}
                <CustomSelect
                  showHeader={false}
                  value={getMultiValues("ctby")}
                  multiSelect={true}
                  placeholder="Search people"
                  options={
                    autoSelectOptionsMap?.["ctby"] ??
                    filterByName["ctby"].options ??
                    []
                  }
                  updatedOptions={
                    autoSelectOptionsMap?.["ctby"] ??
                    filterByName["ctby"].options ??
                    []
                  }
                  creditData={getMultiValues("ctby").map((v, i) => ({
                    value: v,
                    label: getMultiLabels("ctby")[i] ?? v,
                  }))}
                  handleChange={(newValue) => {
                    const picked =
                      (newValue as { value: string; label: string }[]) ?? [];
                    commitMulti(
                      "ctby",
                      picked.map((o) => String(o.value)),
                      picked.map((o) => String(o.label))
                    );
                    onAutoSelectInputChange?.({ name: "ctby", keyword: "" });
                  }}
                  onInputChangeCallback={
                    onAutoSelectInputChange
                      ? (params) =>
                          debouncedAutoSelectInputChange({
                            name: "ctby",
                            keyword: params.keyword,
                          })
                      : undefined
                  }
                  handleBlur={() =>
                    onAutoSelectInputChange?.({ name: "ctby", keyword: "" })
                  }
                  hideArrow={true}
                  loader={true}
                  clearOnSetValue={true}
                />
              </div>
            )}

            {/* Team */}
            {shouldShowField("team") &&
              filterByName["team"] &&
              renderField("team")}

            {shouldShowField("agency") && filterByName["agency"] && (
              <div className="fs-field">
                <label className="fs-field-label">Agency</label>
                {(autoSelectOptionsMap?.["agencyQuick"]?.length ?? 0) > 0 && (
                  <div className="fs-uploader-presets">
                    {autoSelectOptionsMap!["agencyQuick"]
                      .slice(0, 3)
                      .map((opt) => {
                        const isActive = getMultiValues("agency")
                          .map(String)
                          .includes(String(opt.value));
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`text-truncate fs-uploader-chip${
                              isActive ? " is-active" : ""
                            }`}
                            onClick={() =>
                              setAgencyPreset(opt.value, opt.label)
                            }
                            aria-pressed={isActive}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                  </div>
                )}
                <CustomSelect
                  showHeader={false}
                  value={getMultiValues("agency")}
                  multiSelect={true}
                  placeholder="Search agencies"
                  options={
                    autoSelectOptionsMap?.["agency"] ??
                    filterByName["agency"].options ??
                    []
                  }
                  updatedOptions={
                    autoSelectOptionsMap?.["agency"] ??
                    filterByName["agency"].options ??
                    []
                  }
                  creditData={getMultiValues("agency").map((v, i) => ({
                    value: v,
                    label: getMultiLabels("agency")[i] ?? v,
                  }))}
                  handleChange={(newValue) => {
                    const picked =
                      (newValue as { value: string; label: string }[]) ?? [];
                    commitMulti(
                      "agency",
                      picked.map((o) => String(o.value)),
                      picked.map((o) => String(o.label))
                    );
                    onAutoSelectInputChange?.({ name: "agency", keyword: "" });
                  }}
                  onInputChangeCallback={
                    onAutoSelectInputChange
                      ? (params) =>
                          debouncedAutoSelectInputChange({
                            name: "agency",
                            keyword: params.keyword,
                          })
                      : undefined
                  }
                  handleBlur={() =>
                    onAutoSelectInputChange?.({ name: "agency", keyword: "" })
                  }
                  hideArrow={true}
                  loader={true}
                  clearOnSetValue={true}
                />
              </div>
            )}

            {/* Upload date */}
            {shouldShowField("range") && filterByName["range"] && (
              <div className="fs-field">
                <div className="fs-field-row">
                  <label className="fs-field-label">Upload date</label>
                  {getDateValue("range") && (
                    <button
                      type="button"
                      className="fs-field-reset"
                      onClick={() => commitDate("range", null, "")}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <DateRangeField
                  key={getDateValue("range") ? "range-set" : "range-empty"}
                  filter={filterByName["range"]}
                  value={getDateValue("range")}
                  onChange={(range, display) =>
                    commitDate("range", range, display)
                  }
                />
              </div>
            )}

            {/* Content status */}
            {shouldShowField("media_status") &&
              filterByName["media_status"] &&
              renderField("media_status")}

            {/* Content duration — presets + custom range */}
            {shouldShowField("media_duration") &&
              filterByName["media_duration"] && (
                <div className="fs-field">
                  <div className="fs-field-row">
                    <label className="fs-field-label">Content duration</label>
                    {getDateValue("media_duration") && (
                      <button
                        type="button"
                        className="fs-field-reset"
                        onClick={() => commitDate("media_duration", null, "")}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <DurationRangeField
                    value={getDateValue("media_duration")}
                    onChange={(range, display) =>
                      commitDate("media_duration", range, display)
                    }
                  />
                </div>
              )}

            {/* Publishing destinations — 2-col platform grid with logos */}
            {shouldShowField("publishing_destinations") &&
              filterByName["publishing_destinations"] && (
                <div className="fs-field">
                  <div className="fs-field-row">
                    <label className="fs-field-label">Published to</label>
                    {getMultiValues("publishing_destinations").length > 0 && (
                      <button
                        type="button"
                        className="fs-field-reset"
                        onClick={() =>
                          commitMulti("publishing_destinations", [], [])
                        }
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <PlatformGrid
                    filter={filterByName["publishing_destinations"]}
                    values={getMultiValues("publishing_destinations")}
                    labels={getMultiLabels("publishing_destinations")}
                    onChange={(v, l) =>
                      commitMulti("publishing_destinations", v, l)
                    }
                  />
                </div>
              )}

            {/* Content type */}
            {shouldShowField("media_ref") &&
              filterByName["media_ref"] &&
              renderField("media_ref")}

            {/* Content source */}
            {shouldShowField("source") &&
              filterByName["source"] &&
              renderField("source")}

            {/* Remaining facets — flat, no section headings */}
            {SECONDARY_ORDER.filter((k) => shouldShowField(k)).map((k) =>
              renderField(k)
            )}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

// ---------------- TextSearchField ----------------

interface TextSearchFieldProps {
  label: string;
  placeholder: string;
  initialValue: string;
  onCommit: (value: string) => void;
  /** Fired after onCommit when the user presses Enter. The sheet wires this
      to the immediate-apply path so Enter stages then applies in one tick. */
  onSubmit?: () => void;
}

export interface TextSearchFieldHandle {
  /** Commit any pending local edit. Called by the sheet on close. */
  flush: () => void;
}

/**
 * Free-text search input. Owns its local state for snappy typing and
 * auto-commits after 400 ms of inactivity. Also commits on Enter or when
 * the sheet closes via `flush()`.
 */
const TextSearchField = forwardRef<TextSearchFieldHandle, TextSearchFieldProps>(
  ({ label, placeholder, initialValue, onCommit, onSubmit }, ref) => {
    const [value, setValue] = useState(initialValue);

    // Reflect external changes (chip removal, Clear all, tab switch).
    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const debouncedValue = useDebounce(value, 400);

    // Auto-commit after 400 ms of inactivity. Skip when already in sync.
    useEffect(() => {
      if (debouncedValue.trim() === initialValue.trim()) return;
      onCommit(debouncedValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    useImperativeHandle(ref, () => ({
      flush: () => {
        if (value.trim() !== initialValue.trim()) {
          onCommit(value);
        }
      },
    }));

    return (
      <div className="fs-field">
        <label className="fs-field-label">{label}</label>
        <InputGroup size="sm" className="fs-input">
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onCommit(value);
                // onCommit is synchronous on the parent's stagedRef, so by
                // the time onSubmit reads it, the value is already there.
                onSubmit?.();
              }
            }}
            autoComplete="off"
            spellCheck={false}
          />
          {value && (
            <Button
              variant="link"
              className="fs-input-clear"
              onClick={() => {
                setValue("");
                onCommit("");
              }}
              aria-label={`Clear ${label}`}
            >
              <X size={12} />
            </Button>
          )}
        </InputGroup>
      </div>
    );
  }
);
TextSearchField.displayName = "TextSearchField";

// ---------------- MultiPicker ----------------

interface MultiPickerProps {
  filter: Filter;
  values: string[];
  labels: string[];
  onChange: (values: string[], labels: string[]) => void;
  supportsServerSearch?: boolean;
  onServerSearch?: (params: { name: string; keyword: string }) => void;
  dynamicOptions?: FilterOption[];
}

const MultiPicker: React.FC<MultiPickerProps> = ({
  filter,
  values,
  labels,
  onChange,
  supportsServerSearch,
  onServerSearch,
  dynamicOptions,
}) => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!supportsServerSearch || !onServerSearch) return;
    const t = setTimeout(() => {
      onServerSearch({ name: filter.name, keyword: query });
    }, 250);
    return () => clearTimeout(t);
  }, [query, supportsServerSearch, filter.name, onServerSearch]);

  const filtered = useMemo(() => {
    const baseOptions = dynamicOptions ?? filter.options ?? [];
    if (!query) return baseOptions;
    const q = query.toLowerCase();
    return baseOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [dynamicOptions, filter.options, query]);

  const toggle = (option: FilterOption) => {
    const idx = values.indexOf(option.value);
    if (idx >= 0) {
      onChange(
        values.filter((v) => v !== option.value),
        labels.filter((_, i) => i !== idx)
      );
    } else {
      onChange([...values, option.value], [...labels, option.label]);
    }
  };

  return (
    <div className="fs-multi">
      <div className="fs-multi-search">
        <Search size={12} className="fs-multi-search-icon" />
        <input
          type="text"
          className="fs-multi-search-input"
          placeholder={`Search ${filter.displayName.toLowerCase()}`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {values.length > 0 && (
          <button
            type="button"
            className="fs-multi-clear"
            onClick={() => onChange([], [])}
            title="Clear selection"
          >
            Clear
          </button>
        )}
      </div>
      <div className="fs-multi-list" role="listbox">
        {filtered.length === 0 && (
          <div className="fs-multi-empty">No matches</div>
        )}
        {filtered.map((opt) => {
          const selected = values.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={selected}
              className={`fs-multi-item ${selected ? "is-selected" : ""}`}
              onClick={() => toggle(opt)}
            >
              <span className="fs-multi-item-label">{opt.label}</span>
              {selected && <Check size={14} className="fs-multi-item-check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// UnifiedDateTimePicker extended with react-datepicker popper props so the
// FilterSheet can force bottom-start placement without modifying the shared component.
const DatePickerField = UnifiedDateTimePicker as React.FC<
  React.ComponentProps<typeof UnifiedDateTimePicker> & {
    popperPlacement?: string;
    popperProps?: { strategy?: "absolute" | "fixed" };
  }
>;

// ---------------- DateRangeField ----------------

interface DateRangeFieldProps {
  filter: Filter;
  value: any;
  onChange: (range: any, display: string) => void;
}

const DATE_PRESETS: { label: string; days: number }[] = [
  { label: "Today", days: 0 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
];

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  filter,
  value,
  onChange,
}) => {
  const mtOrCt = filter.displayName === "Upload Date" ? "ct" : "mt";

  const [startDate, setStartDate] = useState<Date | null>(
    value?.[mtOrCt]?.gte ? new Date(value[mtOrCt].gte) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    value?.[mtOrCt]?.lte ? new Date(value[mtOrCt].lte) : null
  );

  useEffect(() => {
    setStartDate(value?.[mtOrCt]?.gte ? new Date(value[mtOrCt].gte) : null);
    setEndDate(value?.[mtOrCt]?.lte ? new Date(value[mtOrCt].lte) : null);
  }, [value, mtOrCt]);

  const formatDisplay = (s: Date, e: Date) => {
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    return `${fmt(s)} – ${fmt(e)}`;
  };

  const commitRange = (s: Date, e: Date) => {
    const start = new Date(s);
    start.setHours(0, 0, 0, 0);
    const end = new Date(e);
    end.setHours(23, 59, 59, 999);
    onChange(
      { [mtOrCt]: { gte: start.getTime(), lte: end.getTime() } },
      formatDisplay(start, end)
    );
  };

  const applyPreset = (days: number) => {
    if (isPresetActive(days)) {
      setStartDate(null);
      setEndDate(null);
      onChange(null, "");
      return;
    }
    const end = new Date();
    const start = new Date();
    if (days === 0) {
      start.setHours(0, 0, 0, 0);
    } else {
      start.setDate(end.getDate() - days);
    }
    setStartDate(start);
    setEndDate(end);
    commitRange(start, end);
  };

  const isPresetActive = (days: number): boolean => {
    if (!startDate || !endDate) return false;
    const now = new Date();
    const expStart = new Date(now);
    expStart.setDate(now.getDate() - days);
    expStart.setHours(0, 0, 0, 0);
    const expEnd = new Date(now);
    expEnd.setHours(23, 59, 59, 999);
    const normStart = new Date(startDate);
    normStart.setHours(0, 0, 0, 0);
    const normEnd = new Date(endDate);
    normEnd.setHours(23, 59, 59, 999);
    return normStart.getTime() === expStart.getTime() && normEnd.getTime() === expEnd.getTime();
  };

  const handleStartChange = (date: Date | null) => {
    setStartDate(date);
    if (date && endDate) commitRange(date, endDate);
    else if (!date && !endDate) onChange(null, "");
  };

  const handleEndChange = (date: Date | null) => {
    setEndDate(date);
    if (startDate && date) commitRange(startDate, date);
    else if (!startDate && !date) onChange(null, "");
  };

  return (
    <div className="fs-daterange-v2">
      <div className="fs-daterange-presets-row">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className={`fs-daterange-chip${isPresetActive(p.days) ? " is-active" : ""}`}
            onClick={() => applyPreset(p.days)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="fs-daterange-row">
        <DatePickerField
          mode="date"
          label=""
          value={startDate}
          maxDate={endDate ?? new Date()}
          onChange={handleStartChange}
          placeholder="From"
          popperPlacement="bottom-start"
          popperProps={{ strategy: "fixed" }}
        />
        <DatePickerField
          mode="date"
          label=""
          value={endDate}
          startDate={startDate ?? undefined}
          maxDate={new Date()}
          onChange={handleEndChange}
          placeholder="To"
          popperPlacement="bottom-end"
          popperProps={{ strategy: "fixed" }}
        />
      </div>
    </div>
  );
};

// ---------------- PlatformGrid ----------------

interface PlatformGridProps {
  filter: Filter;
  values: string[];
  labels: string[];
  onChange: (values: string[], labels: string[]) => void;
}

const PlatformGrid: React.FC<PlatformGridProps> = ({
  filter,
  values,
  labels,
  onChange,
}) => {
  const options = filter.options ?? [];
  const toggle = (opt: FilterOption) => {
    const idx = values.indexOf(opt.value);
    if (idx >= 0) {
      onChange(
        values.filter((v) => v !== opt.value),
        labels.filter((_, i) => i !== idx)
      );
    } else {
      onChange([...values, opt.value], [...labels, opt.label]);
    }
  };
  return (
    <div className="fs-platforms">
      {options.map((opt) => {
        const selected = values.includes(opt.value);
        const logo = PUBLISHING_LOGOS[opt.value];
        return (
          <button
            key={opt.value}
            type="button"
            role="checkbox"
            aria-checked={selected}
            className={`fs-platform${selected ? " is-selected" : ""}`}
            onClick={() => toggle(opt)}
          >
            <span className="fs-platform-check" aria-hidden="true">
              {selected && <Check size={12} />}
            </span>
            {logo && (
              <img
                src={logo}
                alt=""
                className="fs-platform-logo"
                draggable={false}
              />
            )}
            <span className="fs-platform-label">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ---------------- TileGrid ----------------

interface TileGridProps {
  options: FilterOption[];
  mode: "single" | "multi";
  values: string[];
  labels: string[];
  onChange: (values: string[], labels: string[]) => void;
  headerNode?: React.ReactNode;
  renderLeading?: (opt: FilterOption) => React.ReactNode;
  /** Show only the first N tiles by default; reveal the rest via "Show all".
      If omitted, all options render upfront. */
  collapseAfter?: number;
  /** When the list is expanded, render a localized search input on top. */
  searchPlaceholder?: string;
  /** If true, show search input even before expanding collapsed tiles. */
  alwaysShowSearch?: boolean;
}

// Generic 2-col tile picker shared by Content Status, Content Source, Team,
// and Content Type. Same visual language as PlatformGrid so the four filters
// read as a matched family.
const TileGrid: React.FC<TileGridProps> = ({
  options,
  mode,
  values,
  labels,
  onChange,
  headerNode,
  renderLeading,
  collapseAfter,
  searchPlaceholder,
  alwaysShowSearch,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");

  const toggle = (opt: FilterOption) => {
    const isSelected = values.includes(opt.value);
    if (mode === "single") {
      if (isSelected) onChange([], []);
      else onChange([opt.value], [opt.label]);
      return;
    }
    if (isSelected) {
      const idx = values.indexOf(opt.value);
      onChange(
        values.filter((v) => v !== opt.value),
        labels.filter((_, i) => i !== idx)
      );
    } else {
      onChange([...values, opt.value], [...labels, opt.label]);
    }
  };

  // When expanded with a search box, filter the option list by the term.
  const visibleOptions = (() => {
    if (collapseAfter == null) return options;
    if (!expanded) {
      if (alwaysShowSearch && search.trim()) {
        const q = search.trim().toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
      }
      return options.slice(0, collapseAfter);
    }
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  })();
  const hiddenCount =
    collapseAfter != null && !expanded
      ? Math.max(0, options.length - collapseAfter)
      : 0;

  return (
    <div className="fs-tilegrid-wrap">
      {headerNode}
      {(expanded || alwaysShowSearch) && searchPlaceholder && (
        <InputGroup size="sm" className="fs-input fs-tilegrid-search-unified">
          <InputGroup.Text>
            <Search size={14} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {search && (
            <Button
              variant="link"
              className="fs-input-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <X size={12} />
            </Button>
          )}
        </InputGroup>
      )}
      <div className="fs-tiles">
        {visibleOptions.length === 0 && (
          <div className="fs-tilegrid-empty">No matches</div>
        )}
        {visibleOptions.map((opt) => {
          const selected = values.includes(opt.value);
          const isRadio = mode === "single";
          return (
            <button
              key={opt.value}
              type="button"
              role={isRadio ? "radio" : "checkbox"}
              aria-checked={selected}
              className={`fs-tile${selected ? " is-selected" : ""}${isRadio ? " fs-tile-radio" : ""}`}
              onClick={() => toggle(opt)}
            >
              <span className="fs-tile-check" aria-hidden="true">
                {!isRadio && selected && <Check size={12} />}
              </span>
              {renderLeading?.(opt)}
              <span className="fs-tile-label">{opt.label}</span>
            </button>
          );
        })}
      </div>
      {collapseAfter != null && (hiddenCount > 0 || expanded) && (
        <button
          type="button"
          className="fs-tilegrid-toggle"
          onClick={() => {
            setExpanded((v) => !v);
            if (expanded) setSearch("");
          }}
        >
          {expanded ? "Show less" : `Show all (${options.length})`}
        </button>
      )}
    </div>
  );
};

// ---------------- DurationRangeField ----------------

// Operator-based duration filter — follows the B2B convention used by Notion,
// Airtable, and Salesforce: one operator dropdown followed by value inputs
// that adapt to the chosen operator, with compact shortcut chips below for
// common ranges. Much less vertical space, more intuitive, more mature.

type DurationOperator = "between" | "under" | "over";

interface DurationOpOption {
  value: DurationOperator;
  label: string;
}
const DURATION_OPS: DurationOpOption[] = [
  { value: "under", label: "is under" },
  { value: "between", label: "is between" },
  { value: "over", label: "is over" },
];

// Quick-pick shortcuts — a single click sets op + values.
interface DurationShortcut {
  label: string;
  op: DurationOperator;
  a?: number; // minutes
  b?: number; // minutes (only for "between")
}
const DURATION_SHORTCUTS: DurationShortcut[] = [
  { label: "< 1m", op: "under", a: 1 },
  { label: "1–3m", op: "between", a: 1, b: 3 },
  { label: "3–5m", op: "between", a: 3, b: 5 },
  { label: "5–10m", op: "between", a: 5, b: 10 },
  { label: "10m+", op: "over", a: 10 },
];

// Normalise a minute-string input into a whole-number-of-seconds. Empty string
// or 0 collapses to `undefined` so an unbounded side is actually unbounded
// (we don't want to store `{gte: 0}` which is noisy on the wire).
function minsStrToSecs(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.round(n * 60);
}

function secsToMinsStr(sec: number | undefined): string {
  if (sec == null) return "";
  // Prefer whole minutes; fall back to 1-decimal if the user picked something
  // sub-minute via the custom input.
  const m = sec / 60;
  return Number.isInteger(m) ? String(m) : m.toFixed(1);
}

function formatDurationRange(
  gteSec: number | undefined,
  lteSec: number | undefined
): string {
  const fromMin = gteSec != null ? Math.round(gteSec / 60) : null;
  const toMin = lteSec != null ? Math.round(lteSec / 60) : null;
  if (fromMin != null && toMin != null) return `${fromMin}–${toMin} min`;
  if (fromMin != null) return `${fromMin}+ min`;
  if (toMin != null) return `Under ${toMin} min`;
  return "";
}

// Derive local component state from the applied range.
function deriveDurationState(
  appliedGte: number | undefined,
  appliedLte: number | undefined
): { op: DurationOperator; a: string; b: string } {
  if (appliedGte != null && appliedLte != null) {
    return {
      op: "between",
      a: secsToMinsStr(appliedGte),
      b: secsToMinsStr(appliedLte),
    };
  }
  if (appliedLte != null) {
    return { op: "under", a: secsToMinsStr(appliedLte), b: "" };
  }
  if (appliedGte != null) {
    return { op: "over", a: secsToMinsStr(appliedGte), b: "" };
  }
  return { op: "between", a: "", b: "" };
}

interface DurationRangeFieldProps {
  value: any; // { "meta.duration": { gte?: number; lte?: number } } | undefined
  onChange: (range: any, display: string) => void;
}

const DurationRangeField: React.FC<DurationRangeFieldProps> = ({
  value,
  onChange,
}) => {
  const appliedInner = value?.["meta.duration"] ?? null;
  const appliedGte: number | undefined = appliedInner?.gte;
  const appliedLte: number | undefined = appliedInner?.lte;

  const init = deriveDurationState(appliedGte, appliedLte);
  const [op, setOp] = useState<DurationOperator>(init.op);
  const [valA, setValA] = useState(init.a);
  const [valB, setValB] = useState(init.b);

  // Resync when applied value changes externally (chip removal, Clear all).
  useEffect(() => {
    const s = deriveDurationState(appliedGte, appliedLte);
    if (!appliedInner) {
      // External clear — reset everything including the operator.
      setOp(s.op);
      setValA(s.a);
      setValB(s.b);
    } else {
      // Mid-edit partial commit or external value change — sync the displayed
      // values but leave op alone so a partial "between" state (only one bound
      // filled) doesn't flip the operator to "over" or "under".
      setValA(s.a);
      setValB(s.b);
    }
  }, [appliedGte, appliedLte, appliedInner]);

  const commitTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(commitTimerRef.current), []);

  // Commit helper — builds the range object from op + minute strings.
  const commitRange = useCallback(
    (nextOp: DurationOperator, a: string, b: string) => {
      let gte: number | undefined;
      let lte: number | undefined;
      if (nextOp === "between") {
        gte = minsStrToSecs(a);
        lte = minsStrToSecs(b);
      } else if (nextOp === "under") {
        lte = minsStrToSecs(a);
      } else {
        gte = minsStrToSecs(a);
      }
      if (gte == null && lte == null) {
        onChange(null, "");
        return;
      }
      const inner: { gte?: number; lte?: number } = {};
      if (gte != null) inner.gte = gte;
      if (lte != null) inner.lte = lte;
      onChange({ "meta.duration": inner }, formatDurationRange(gte, lte));
    },
    [onChange]
  );

  const handleOpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextOp = e.target.value as DurationOperator;
    setOp(nextOp);
    if (nextOp !== "between") setValB("");
    commitRange(nextOp, valA, nextOp === "between" ? valB : "");
  };

  const handleValAChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setValA(raw);
    clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => commitRange(op, raw, valB), 400);
  };

  const handleValBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setValB(raw);
    clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => commitRange(op, valA, raw), 400);
  };

  const applyShortcut = (s: DurationShortcut) => {
    if (isShortcutActive(s)) {
      setOp("between");
      setValA("");
      setValB("");
      onChange(null, "");
      return;
    }
    const a = s.a != null ? String(s.a) : "";
    const b = s.b != null ? String(s.b) : "";
    setOp(s.op);
    setValA(a);
    setValB(b);
    commitRange(s.op, a, b);
  };

  // A shortcut is "active" when its operator shape + values match applied.
  const isShortcutActive = (s: DurationShortcut): boolean => {
    const aSec = s.a != null ? s.a * 60 : undefined;
    const bSec = s.b != null ? s.b * 60 : undefined;
    if (s.op === "between") return appliedGte === aSec && appliedLte === bSec;
    if (s.op === "under") return appliedGte == null && appliedLte === aSec;
    return appliedGte === aSec && appliedLte == null;
  };

  return (
    <div className="fs-duration">
      {/* Shortcut chips — one-click common ranges */}
      <div className="fs-duration-shortcuts">
        {DURATION_SHORTCUTS.map((s) => (
          <button
            key={s.label}
            type="button"
            className={`fs-duration-chip${isShortcutActive(s) ? " is-active" : ""}`}
            onClick={() => applyShortcut(s)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Operator row — dropdown + adaptive value inputs */}
      <div className="fs-duration-row">
        <select
          className="fs-duration-op"
          value={op}
          onChange={handleOpChange}
          aria-label="Duration operator"
        >
          {DURATION_OPS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          className="fs-duration-num"
          min={0}
          step={1}
          inputMode="numeric"
          placeholder={op === "between" ? "Min" : "0"}
          value={valA}
          onChange={handleValAChange}
          aria-label={
            op === "under"
              ? "Maximum duration in minutes"
              : "Minimum duration in minutes"
          }
        />
        {op === "between" && (
          <>
            <span className="fs-duration-word" aria-hidden="true">
              –
            </span>
            <input
              type="number"
              className="fs-duration-num"
              min={0}
              step={1}
              inputMode="numeric"
              placeholder="Max"
              value={valB}
              onChange={handleValBChange}
              aria-label="Maximum duration in minutes"
            />
          </>
        )}
        <span className="fs-duration-unit">min</span>
      </div>
    </div>
  );
};

export default FilterSheet;
